import React, { useEffect, useMemo, useState } from 'react';
import {
  hqRevenueService,
  RevenueByStore,
  StoreRevenue,
  StoreMonthly,
} from '../services/hqRevenueService';

const won = (n: number) => '₩' + (n || 0).toLocaleString('ko-KR');
const eok = (n: number) => {
  if (!n) return '0원';
  const man = Math.round(n / 10000);
  return man.toLocaleString('ko-KR') + '만원';
};

const thisMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// 전월 대비 % (지난달 0이면 null)
const momPct = (cur: number, prev: number) => (prev ? Math.round(((cur - prev) / prev) * 100) : null);

type SortKey = 'this_desc' | 'this_asc' | 'drop' | 'cum_desc' | 'name';

const SORT_LABEL: Record<SortKey, string> = {
  this_desc: '이번달 매출 높은순',
  this_asc: '이번달 매출 낮은순',
  drop: '급감순 (전월 대비)',
  cum_desc: '누적 높은순',
  name: '이름순',
};

const HqRevenuePage: React.FC = () => {
  const [month, setMonth] = useState<string>(thisMonthStr());
  const [data, setData] = useState<RevenueByStore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 리스트 조작
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('this_desc');
  const [showEmpty, setShowEmpty] = useState(false); // 0원(매출 이력 없는) 매장 표시

  // 드릴다운
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [monthlyCache, setMonthlyCache] = useState<Record<string, StoreMonthly>>({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await hqRevenueService.byStore(month));
    } catch (e: any) {
      setError(
        e?.response?.status === 403
          ? '매출은 본부 대표만 볼 수 있습니다.'
          : e?.response?.data?.detail || '매출을 불러오지 못했습니다.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const toggleExpand = async (orgId: string) => {
    if (expandedId === orgId) return setExpandedId(null);
    setExpandedId(orgId);
    if (!monthlyCache[orgId]) {
      try {
        const m = await hqRevenueService.storeMonthly(orgId, 6);
        setMonthlyCache((prev) => ({ ...prev, [orgId]: m }));
      } catch {
        /* 무시 */
      }
    }
  };

  const allStores = data?.stores ?? [];

  // 매출 이력이 있는(=한 번이라도 판매) 매장만 "집계 대상" 으로 봄
  const activeStores = useMemo(() => allStores.filter((s) => s.cumulative > 0), [allStores]);

  // ⚠️ 주의 매장: ① 이번달 매출 끊김(과거엔 있었는데 0원) ② 전월 대비 20%+ 하락
  const attention = useMemo(() => {
    const items: { store: StoreRevenue; reason: string; severity: number }[] = [];
    for (const s of allStores) {
      if (s.this_month === 0 && s.cumulative > 0) {
        items.push({ store: s, reason: '이번달 매출 0원 (과거엔 판매 있었음)', severity: 1000 });
      } else {
        const p = momPct(s.this_month, s.last_month);
        if (p !== null && p <= -20) {
          items.push({ store: s, reason: `전월 대비 ${p}%`, severity: -p });
        }
      }
    }
    return items.sort((a, b) => b.severity - a.severity);
  }, [allStores]);

  // 요약
  const summary = useMemo(() => {
    const cnt = activeStores.length;
    const top = activeStores.reduce<StoreRevenue | null>(
      (best, s) => (!best || s.this_month > best.this_month ? s : best),
      null,
    );
    // 가장 많이 빠진 곳 (전월 대비 % 최저)
    let worst: { store: StoreRevenue; pct: number } | null = null;
    for (const s of activeStores) {
      const p = momPct(s.this_month, s.last_month);
      if (p !== null && (worst === null || p < worst.pct)) worst = { store: s, pct: p };
    }
    return {
      cnt,
      avg: cnt ? Math.round((data?.totals.this_month ?? 0) / cnt) : 0,
      top,
      worst: worst && worst.pct < 0 ? worst : null,
    };
  }, [activeStores, data]);

  // 리스트: 검색 + 0원 숨김 + 정렬
  const visible = useMemo(() => {
    let list = allStores;
    if (!showEmpty) list = list.filter((s) => s.cumulative > 0 || s.this_month > 0);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((s) => s.name.toLowerCase().includes(q));
    const arr = [...list];
    arr.sort((a, b) => {
      switch (sortKey) {
        case 'this_asc':
          return a.this_month - b.this_month;
        case 'cum_desc':
          return b.cumulative - a.cumulative;
        case 'name':
          return a.name.localeCompare(b.name, 'ko');
        case 'drop': {
          const pa = momPct(a.this_month, a.last_month) ?? 9999;
          const pb = momPct(b.this_month, b.last_month) ?? 9999;
          return pa - pb; // 많이 빠진 순
        }
        case 'this_desc':
        default:
          return b.this_month - a.this_month;
      }
    });
    return arr;
  }, [allStores, showEmpty, search, sortKey]);

  const maxThis = Math.max(1, ...visible.map((s) => s.this_month));
  const hiddenEmptyCount = allStores.filter((s) => s.cumulative === 0 && s.this_month === 0).length;

  return (
    <div className="sm:p-6 max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800">💰 점포별 매출</h1>
        <input
          type="month"
          value={month}
          max={thisMonthStr()}
          onChange={(e) => setMonth(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
        />
      </div>
      <p className="text-xs text-slate-500 mb-5">
        가맹점 자체 매출(이용권 판매, 취소 제외) · 구독형(클라우드) 매장 기준 · 독립 서버 가맹점은 집계 대상 아님
      </p>

      {loading && <div className="text-slate-500 py-10 text-center">불러오는 중…</div>}
      {error && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>}

      {!loading && !error && data && (
        <>
          {/* 요약 밴드 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <SummaryCard label={`이번달 합계 (${data.month})`} value={eok(data.totals.this_month)} highlight />
            <SummaryCard label={`매장당 평균 · ${summary.cnt}곳`} value={eok(summary.avg)} />
            <SummaryCard
              label="이번달 1등"
              value={summary.top ? summary.top.name : '—'}
              sub={summary.top ? eok(summary.top.this_month) : undefined}
            />
            <SummaryCard
              label="전월 대비 최대 하락"
              value={summary.worst ? summary.worst.store.name : '—'}
              sub={summary.worst ? `${summary.worst.pct}%` : '없음'}
              danger={!!summary.worst}
            />
          </div>

          {/* ⚠️ 주의 매장 */}
          {attention.length > 0 && (
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="text-sm font-semibold text-amber-800 mb-2">
                ⚠️ 주의가 필요한 매장 ({attention.length})
              </div>
              <div className="space-y-1.5">
                {attention.slice(0, 6).map(({ store, reason }) => (
                  <button
                    key={store.organization_id}
                    onClick={() => {
                      setSearch(store.name);
                      setExpandedId(store.organization_id);
                    }}
                    className="w-full flex items-center justify-between text-sm text-left hover:bg-amber-100 rounded px-2 py-1"
                  >
                    <span className="font-medium text-slate-800">{store.name}</span>
                    <span className="text-amber-700">
                      {reason} · 이번달 {eok(store.this_month)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 검색 + 정렬 + 0원 토글 */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="매장 이름 검색…"
              className="flex-1 min-w-[180px] border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
            />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white"
            >
              {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => (
                <option key={k} value={k}>
                  {SORT_LABEL[k]}
                </option>
              ))}
            </select>
            {hiddenEmptyCount > 0 && (
              <label className="flex items-center gap-1.5 text-xs text-slate-500 select-none cursor-pointer">
                <input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} />
                0원 매장 보기 ({hiddenEmptyCount})
              </label>
            )}
          </div>

          {/* 표 */}
          {visible.length === 0 ? (
            <div className="text-slate-500 py-10 text-center">
              {search ? '검색 결과가 없습니다.' : '집계할 매장이 없습니다.'}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">점포 ({visible.length})</th>
                    <th className="text-right px-4 py-2 font-medium">이번달</th>
                    <th className="text-right px-4 py-2 font-medium">전월 대비</th>
                    <th className="text-right px-4 py-2 font-medium">지난달</th>
                    <th className="text-right px-4 py-2 font-medium">누적</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((s) => {
                    const pct = momPct(s.this_month, s.last_month);
                    const expanded = expandedId === s.organization_id;
                    const monthly = monthlyCache[s.organization_id];
                    return (
                      <React.Fragment key={s.organization_id}>
                        <tr
                          className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => toggleExpand(s.organization_id)}
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-800">
                              <span className="text-slate-400 mr-1">{expanded ? '▾' : '▸'}</span>
                              {s.name}
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded mt-1.5 overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded"
                                style={{ width: `${(s.this_month / maxThis) * 100}%` }}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="font-semibold text-slate-800">{eok(s.this_month)}</div>
                            <div className="text-[11px] text-slate-400">{s.this_month_count}건</div>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {pct === null ? (
                              <span className="text-slate-300">—</span>
                            ) : (
                              <span className={pct >= 0 ? 'text-emerald-600 font-medium' : 'text-red-500 font-medium'}>
                                {pct >= 0 ? '▲' : '▼'} {Math.abs(pct)}%
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-500 whitespace-nowrap">{eok(s.last_month)}</td>
                          <td className="px-4 py-3 text-right text-slate-700 whitespace-nowrap">{eok(s.cumulative)}</td>
                        </tr>
                        {expanded && (
                          <tr className="bg-slate-50/70">
                            <td colSpan={5} className="px-4 py-3">
                              {monthly ? <MonthlyBars monthly={monthly} /> : (
                                <div className="text-xs text-slate-400 py-2">추이 불러오는 중…</div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                    <td className="px-4 py-3 text-slate-700">합계 (전체 {activeStores.length}곳)</td>
                    <td className="px-4 py-3 text-right">{eok(data.totals.this_month)}</td>
                    <td />
                    <td className="px-4 py-3 text-right text-slate-500">{eok(data.totals.last_month)}</td>
                    <td className="px-4 py-3 text-right">{eok(data.totals.cumulative)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-3">
            합계는 검색·필터와 무관하게 전체 매장 기준입니다. 숫자에 마우스를 올리면 원 단위로 볼 수 있습니다.
          </p>
        </>
      )}
    </div>
  );
};

const SummaryCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
  danger?: boolean;
}> = ({ label, value, sub, highlight, danger }) => (
  <div
    className={`rounded-xl px-4 py-3 border ${
      highlight ? 'bg-blue-50 border-blue-200' : danger ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'
    }`}
  >
    <div className="text-xs text-slate-500">{label}</div>
    <div
      className={`text-lg font-bold mt-0.5 truncate ${
        highlight ? 'text-blue-700' : danger ? 'text-red-600' : 'text-slate-800'
      }`}
      title={value}
    >
      {value}
    </div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const MonthlyBars: React.FC<{ monthly: StoreMonthly }> = ({ monthly }) => {
  const series = monthly.series;
  if (!series.length) return <div className="text-xs text-slate-400 py-2">최근 매출 기록이 없습니다.</div>;
  const max = Math.max(1, ...series.map((p) => p.amount));
  return (
    <div>
      <div className="text-xs text-slate-500 mb-2">최근 {series.length}개월 추이</div>
      <div className="flex items-end gap-3 h-24">
        {series.map((p) => (
          <div key={p.month} className="flex flex-col items-center flex-1" title={won(p.amount)}>
            <div className="w-full flex items-end justify-center" style={{ height: '68px' }}>
              <div className="w-6 bg-blue-400 rounded-t" style={{ height: `${Math.max(4, (p.amount / max) * 68)}px` }} />
            </div>
            <div className="text-[10px] text-slate-500 mt-1">{p.month.slice(5)}월</div>
            <div className="text-[10px] text-slate-400">{eok(p.amount)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HqRevenuePage;
